<template>
  <div class="col-md-6 d-md-flex justify-content-md-center align-items-md-center mobile-form-padding">
    <div class="col-md-9 akkurate-box">
      <div class="mb-4">
        <h2 class="mb-4 akkurate-dark fw-bold" style="display: flex; justify-content: center">
          <img height="40px" src="../../../assets/img/akkurate_logo.png" />
          AKKURATEBanc
        </h2>
        <p class="akkurate-light-dark" style="display: flex; justify-content: center">
          {{ step === 1 ? "Welcome" : "Two-factor verification" }}
        </p>
      </div>

      <!-- Step 1: username / password -->
      <b-form v-if="step === 1" v-on:submit="onSubmit" v-on:reset="onReset">
        <b-alert class="mt-4" variant="danger" fade :show="showDismissibleAlert">
          {{ error_msg }}
        </b-alert>
        <b-form-group id="username-group" label="Username" label-for="input-1" class="akkurate-dark">
          <b-input-group class="mb-3 mt-2 field-container">
            <b-form-input v-model="form.username" placeholder="Enter username" class="field-main" required />
            <font-awesome-icon
              :icon="['fas', 'user-secret']"
              class="field-icon akkurate-dark position-absolute top-50 end-0 translate-middle-y me-3"
            />
          </b-input-group>
          <errors v-if="form.errors.username">
            {{ form.errors.username[0] }}
          </errors>
        </b-form-group>
        <b-form-group id="password-group" label="Password" label-for="password" class="akkurate-dark">
          <b-input-group class="mt-2 mb-4 field-container">
            <b-form-input
              v-model="form.password"
              id="password"
              placeholder="Enter password"
              class="field-main"
              type="password"
              required
            />
            <a v-on:click="passwordView" type="button">
              <font-awesome-icon
                :icon="['fas', 'eye']"
                class="field-icon awesome-eye akkurate-dark position-absolute top-50 end-0 translate-middle-y me-3"
              />
            </a>
          </b-input-group>
          <errors v-if="form.errors.password">
            {{ form.errors.password[0] }}
          </errors>
        </b-form-group>
        <div class="d-grid gap-2">
          <b-button
            class="fw-semibold akkurate-green-btn akkurate-auth-size"
            :disabled="is_processing"
            type="submit"
            variant="primary"
          >
            <b-spinner class="d-none process-spin" small />
            <span class="loading">Login</span>
          </b-button>
        </div>
      </b-form>

      <!-- Step 2: 2FA code -->
      <div v-else>
        <b-alert show variant="secondary" class="akkurate-2fa-banner">
          {{ twoFactor.message || "Enter your verification code to continue." }}
        </b-alert>
        <p v-if="twoFactor.hint" class="small text-muted">{{ twoFactor.hint }}</p>
        <p v-if="twoFactor.method === 'sms' && twoFactor.masked_phone" class="small">
          Code sent to <strong>{{ twoFactor.masked_phone }}</strong>
        </p>
        <p v-if="countdownLabel" class="small fw-bold text-success">{{ countdownLabel }}</p>

        <b-alert class="mt-2" variant="danger" fade :show="!!twoFactorError">
          {{ twoFactorError }}
        </b-alert>

        <b-form @submit.prevent="verifyTwoFactor">
          <b-form-group :label="twoFactorCodeLabel" label-for="tfa-code" class="akkurate-dark mt-3">
            <b-input-group class="mb-3 mt-2 field-container">
              <b-form-input
                id="tfa-code"
                v-model="twoFactor.code"
                class="field-main"
                :placeholder="twoFactor.method === 'sms' ? 'SMS code' : 'App code'"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                required
              />
              <font-awesome-icon
                :icon="['fas', 'key']"
                class="field-icon akkurate-dark position-absolute top-50 end-0 translate-middle-y me-3"
              />
            </b-input-group>
          </b-form-group>
          <div class="d-grid gap-2">
            <b-button
              class="fw-semibold akkurate-green-btn akkurate-auth-size"
              :disabled="is_verifying || twoFactor.code.replace(/\s/g, '').length < 6"
              type="submit"
              variant="primary"
            >
              <b-spinner v-if="is_verifying" small class="me-1" />
              Verify &amp; continue
            </b-button>
            <b-button variant="outline-secondary" class="mt-1" @click="backToPassword">
              Back to username / password
            </b-button>
          </div>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import JwtService from "@/core/services/jwt.service";
import TitleMixin from "@/mixins/TitleMixin";

function unwrapPayload(res) {
  if (!res || !res.data) return {};
  return res.data.data !== undefined ? res.data.data : res.data;
}

function format422(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "Request failed";
  if (data.message && typeof data.message === "string") return data.message;
  if (data.errors && typeof data.errors === "object") {
    const parts = [];
    Object.keys(data.errors).forEach((k) => {
      const v = data.errors[k];
      if (Array.isArray(v)) v.forEach((m) => parts.push(`${m}`));
      else if (v) parts.push(`${v}`);
    });
    if (parts.length) return parts.join(" ");
  }
  return "Validation failed";
}

export default {
  name: "LoginForm",
  mixins: [TitleMixin],
  data() {
    return this.initialState();
  },
  computed: {
    twoFactorCodeLabel() {
      return this.twoFactor.method === "sms" ? "SMS code (6 digits)" : "Authenticator code (6 digits)";
    },
    countdownLabel() {
      if (this.step !== 2 || this.twoFactor.expiresAt <= 0) return "";
      const s = this.twoFactor.secondsLeft;
      if (s <= 0) return "This step may have expired — try verifying or start over.";
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `Time remaining: ${m}:${String(r).padStart(2, "0")}`;
    },
  },
  watch: {
    step(newVal) {
      if (newVal === 2) {
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
    },
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    passwordView() {
      const el = this.$el.querySelector("#password");
      if (!el) return;
      if (el.type === "password") {
        el.type = "text";
        const eye = this.$el.querySelector(".awesome-eye");
        if (eye) eye.style.color = "rgb(1,134,115)";
      } else {
        el.type = "password";
        const eye = this.$el.querySelector(".awesome-eye");
        if (eye) eye.style.color = "#212529";
      }
    },
    onSubmit(event) {
      event.preventDefault();
      this.doLogin();
    },
    onReset() {},
    startCountdown() {
      this.stopCountdown();
      const mins = parseInt(this.twoFactor.otp_validity_minutes, 10) || 0;
      const sec = Math.max(0, mins * 60);
      this.twoFactor.expiresAt = Date.now() + sec * 1000;
      this.twoFactor.secondsLeft = sec;
      this._tick = setInterval(() => {
        const left = Math.max(0, Math.floor((this.twoFactor.expiresAt - Date.now()) / 1000));
        this.twoFactor.secondsLeft = left;
        if (left <= 0) {
          this.stopCountdown();
        }
      }, 1000);
    },
    stopCountdown() {
      if (this._tick) {
        clearInterval(this._tick);
        this._tick = null;
      }
    },
    backToPassword() {
      this.step = 1;
      this.twoFactorError = "";
      this.twoFactor.pre_auth_token = "";
      this.twoFactor.code = "";
      this.twoFactor.message = "";
      this.showDismissibleAlert = false;
      this.error_msg = "";
    },
    async applySessionFromPayload(payload) {
      const token = payload.token;
      if (!token) return false;
      JwtService.saveToken(token);
      try {
        await this.$store.dispatch("FETCH_USER_PERMISSIONS");
      } catch (e) {
        // no-op
      }
      this.$router.push({ path: "/" });
      return true;
    },
    async doLogin() {
      this.is_processing = true;
      this.showDismissibleAlert = false;
      this.error_msg = "";
      this.form.errors = {};
      this.$Progress.start();
      const spin = this.$el.querySelector(".process-spin");
      const label = this.$el.querySelector(".loading");
      if (spin) spin.classList.remove("d-none");
      if (label) label.textContent = " processing...";

      let result;
      try {
        result = await ApiService.post("auth/login", {
          username: this.form.username,
          password: this.form.password,
        });
      } catch (error) {
        this.$Progress.fail();
        this.is_processing = false;
        if (spin) spin.classList.add("d-none");
        if (label) label.textContent = "Login";
        const st = error?.response?.status;
        this.showDismissibleAlert = true;
        this.error_msg = format422(error);
        if (error?.response?.data?.errors) {
          this.form.errors = error.response.data.errors;
        }
        if (st === 422 && !this.error_msg) {
          this.error_msg = "Invalid username or password.";
        }
        return;
      }

      this.$Progress.finish();
      this.is_processing = false;
      if (spin) spin.classList.add("d-none");
      if (label) label.textContent = "Login";

      const payload = unwrapPayload(result);

      if (payload.requires_two_factor) {
        if (!payload.pre_auth_token) {
          this.showDismissibleAlert = true;
          this.error_msg = "Two-factor sign-in could not continue. Please try again or contact support.";
          return;
        }
        this.step = 2;
        this.twoFactor.pre_auth_token = payload.pre_auth_token || "";
        this.twoFactor.method = payload.two_factor_method === "sms" ? "sms" : "authenticator";
        this.twoFactor.otp_validity_minutes = payload.otp_validity_minutes ?? 10;
        this.twoFactor.masked_phone = payload.masked_phone || "";
        this.twoFactor.message = payload.message || "";
        const mins = this.twoFactor.otp_validity_minutes;
        this.twoFactor.hint = `Complete this step within about ${mins} minute${mins === 1 ? "" : "s"} (institution policy).`;
        this.twoFactor.code = "";
        this.twoFactorError = "";
        return;
      }

      await this.applySessionFromPayload(payload);
    },
    async verifyTwoFactor() {
      const code = String(this.twoFactor.code || "").replace(/\s/g, "");
      if (code.length !== 6) return;
      this.is_verifying = true;
      this.twoFactorError = "";
      this.$Progress.start();
      try {
        const res = await ApiService.post("auth/two-factor/verify", {
          pre_auth_token: this.twoFactor.pre_auth_token,
          code,
        });
        const payload = unwrapPayload(res);
        await this.applySessionFromPayload(payload);
      } catch (e) {
        this.$Progress.fail();
        const msg = format422(e);
        this.twoFactorError = msg;
        if (e?.response?.status === 422 && /expired|expire/i.test(msg)) {
          this.twoFactorError = `${msg} Please use “Back to username / password” and sign in again.`;
        }
      } finally {
        this.is_verifying = false;
        this.$Progress.finish();
      }
    },
    initialState() {
      return {
        step: 1,
        form: {
          username: "",
          password: "",
          errors: {},
        },
        twoFactor: {
          pre_auth_token: "",
          method: "authenticator",
          otp_validity_minutes: 10,
          masked_phone: "",
          message: "",
          hint: "",
          code: "",
          expiresAt: 0,
          secondsLeft: 0,
        },
        twoFactorError: "",
        showDismissibleAlert: false,
        error_msg: "",
        is_processing: false,
        is_verifying: false,
        _tick: null,
      };
    },
    reset() {
      Object.assign(this.$data, this.initialState());
    },
  },
  mounted() {
    const token = localStorage.getItem("token");
    if (token) {
      this.$router.push({ path: "/" });
    }
  },
};
</script>

<style scoped>
.akkurate-2fa-banner {
  border-left: 4px solid rgb(1, 134, 115);
}
</style>
